import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectProcessedBookingsData } from '../redux/slices/bookingsLogSlice';
import { selectApiUrls } from '../redux/slices/apiUrlSlice';
import axiosInstance from '../axios/axiosConfig';

const useJobNumberLogic = () => {
  const bookingsData = useSelector(selectProcessedBookingsData);
  const apiUrls = useSelector(selectApiUrls);

  // Function to fetch fresh GeneralProject data for real-time accuracy
  const fetchFreshJobNumbers = useCallback(async () => {
    try {
      const response = await axiosInstance.get(apiUrls.generalProject);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch fresh GeneralProject data:', error);
      throw error;
    }
  }, [apiUrls.generalProject]);

  // Parse and analyze existing job numbers - can work with fresh data or cached data
  const analyzeJobNumbers = useCallback((dataSource) => {
    const currentYear = new Date().getFullYear();
    const validJobNumbers = [];
    const allJobNumbers = new Set(); // Track all numbers for duplicate detection

    dataSource.forEach(job => {
      const jobNum = job.JobNumber?.toString().trim();
      if (!jobNum) return;

      // Add to all job numbers set for duplicate detection
      allJobNumbers.add(jobNum);

      // Only process numbers starting with "20" (valid years)
      if (!jobNum.startsWith('20')) return;
      
      // Handle child numbers (2025123-00 -> 2025123)
      const baseNumber = jobNum.split('-')[0];
      
      // Validate format (should be 7 digits total)
      if (baseNumber.length === 7 && /^\d{7}$/.test(baseNumber)) {
        const year = parseInt(baseNumber.substring(0, 4));
        const sequence = parseInt(baseNumber.substring(4));
        
        // Reasonable year range check
        if (year >= 2020 && year <= currentYear + 1) {
          const hasChildNumbers = jobNum.includes('-');
          let isChildNumber = false;
          
          // If it has a dash, check if it's a child number
          if (hasChildNumbers) {
            const childSuffix = jobNum.split('-')[1];
            // -00 is considered Primary, -01, -02, etc. are Child
            isChildNumber = childSuffix !== '00';
          }
          
          validJobNumbers.push({ 
            baseNumber, 
            year, 
            sequence, 
            original: jobNum, 
            hasChildNumbers,
            isChildNumber,
            projectId: job.ProjectID
          });
        }
      }
    });

    // Sort by year (desc) then sequence (desc) to get most recent first
    const sorted = validJobNumbers.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.sequence - a.sequence;
    });

    return { validJobNumbers: sorted, allJobNumbers };
  }, []);

  // Cached analysis from current Redux data
  const jobNumberAnalysis = useMemo(() => {
    return analyzeJobNumbers(bookingsData);
  }, [bookingsData, analyzeJobNumbers]);

  // Get next suggested job number for a given year
  const getNextJobNumber = useCallback((year = new Date().getFullYear()) => {
    const yearJobNumbers = jobNumberAnalysis.validJobNumbers.filter(job => job.year === year);
    const highestSequence = yearJobNumbers.length > 0 ? yearJobNumbers[0].sequence : -1;
    const nextSequence = (highestSequence + 1).toString().padStart(3, '0');
    return `${year}${nextSequence}`;
  }, [jobNumberAnalysis.validJobNumbers]);

  // Get next suggested job number using fresh data
  const getNextJobNumberWithFreshData = useCallback(async (year = new Date().getFullYear()) => {
    try {
      const freshData = await fetchFreshJobNumbers();
      const freshAnalysis = analyzeJobNumbers(freshData);
      const yearJobNumbers = freshAnalysis.validJobNumbers.filter(job => job.year === year);
      const highestSequence = yearJobNumbers.length > 0 ? yearJobNumbers[0].sequence : -1;
      const nextSequence = (highestSequence + 1).toString().padStart(3, '0');
      return `${year}${nextSequence}`;
    } catch (error) {
      console.error('Failed to get fresh job number, falling back to cached data:', error);
      return getNextJobNumber(year);
    }
  }, [fetchFreshJobNumbers, analyzeJobNumbers, getNextJobNumber]);

  // Get last N job numbers for display
  const getRecentJobNumbers = useCallback((count = 5) => {
    return jobNumberAnalysis.validJobNumbers.slice(0, count);
  }, [jobNumberAnalysis.validJobNumbers]);

  // Get recent job numbers using fresh data
  const getRecentJobNumbersWithFreshData = useCallback(async (count = 5) => {
    try {
      const freshData = await fetchFreshJobNumbers();
      const freshAnalysis = analyzeJobNumbers(freshData);
      return freshAnalysis.validJobNumbers.slice(0, count);
    } catch (error) {
      console.error('Failed to get fresh recent job numbers, falling back to cached data:', error);
      return getRecentJobNumbers(count);
    }
  }, [fetchFreshJobNumbers, analyzeJobNumbers, getRecentJobNumbers]);

  // Validate a job number
  const validateJobNumber = useCallback((jobNumber) => {
    const errors = [];
    const warnings = [];

    if (!jobNumber || !jobNumber.toString().trim()) {
      errors.push('Job number is required');
      return { isValid: false, errors, warnings };
    }

    const jobNumStr = jobNumber.toString().trim();

    // Check if already exists
    if (jobNumberAnalysis.allJobNumbers.has(jobNumStr)) {
      errors.push('This job number already exists');
    }

    // Handle child numbers for validation
    const baseNumber = jobNumStr.split('-')[0];

    // Check format for base number
    if (baseNumber.length !== 7 || !/^\d{7}$/.test(baseNumber)) {
      if (jobNumStr.startsWith('20')) {
        errors.push('Job number must be in format YYYY### (7 digits total)');
      } else {
        warnings.push('Non-standard job number format (will not follow sequential numbering)');
        return { isValid: true, errors: [], warnings }; // Allow non-standard formats
      }
    } else {
      const year = parseInt(baseNumber.substring(0, 4));
      const sequence = parseInt(baseNumber.substring(4));
      const currentYear = new Date().getFullYear();

      // Year validation
      if (year < 2020 || year > currentYear + 1) {
        warnings.push(`Unusual year: ${year}. Expected range: 2020-${currentYear + 1}`);
      }

      // Check for gaps in sequence
      if (year === currentYear) {
        const yearJobNumbers = jobNumberAnalysis.validJobNumbers.filter(job => job.year === year);
        const nextExpected = yearJobNumbers.length > 0 ? yearJobNumbers[0].sequence + 1 : 0;
        
        if (sequence > nextExpected + 10) {
          warnings.push(`Large gap in sequence. Next expected: ${year}${nextExpected.toString().padStart(3, '0')}`);
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }, [jobNumberAnalysis.allJobNumbers, jobNumberAnalysis.validJobNumbers]);

  // Validate job number using fresh data
  const validateJobNumberWithFreshData = useCallback(async (jobNumber) => {
    try {
      const freshData = await fetchFreshJobNumbers();
      const freshAnalysis = analyzeJobNumbers(freshData);
      
      const errors = [];
      const warnings = [];

      if (!jobNumber || !jobNumber.toString().trim()) {
        errors.push('Job number is required');
        return { isValid: false, errors, warnings };
      }

      const jobNumStr = jobNumber.toString().trim();

      // Check if already exists using fresh data
      if (freshAnalysis.allJobNumbers.has(jobNumStr)) {
        errors.push('This job number already exists');
      }

      // Handle child numbers for validation
      const baseNumber = jobNumStr.split('-')[0];

      // Check format for base number
      if (baseNumber.length !== 7 || !/^\d{7}$/.test(baseNumber)) {
        if (jobNumStr.startsWith('20')) {
          errors.push('Job number must be in format YYYY### (7 digits total)');
        } else {
          warnings.push('Non-standard job number format (will not follow sequential numbering)');
          return { isValid: true, errors: [], warnings }; // Allow non-standard formats
        }
      } else {
        const year = parseInt(baseNumber.substring(0, 4));
        const sequence = parseInt(baseNumber.substring(4));
        const currentYear = new Date().getFullYear();

        // Year validation
        if (year < 2020 || year > currentYear + 1) {
          warnings.push(`Unusual year: ${year}. Expected range: 2020-${currentYear + 1}`);
        }

        // Check for gaps in sequence using fresh data
        if (year === currentYear) {
          const yearJobNumbers = freshAnalysis.validJobNumbers.filter(job => job.year === year);
          const nextExpected = yearJobNumbers.length > 0 ? yearJobNumbers[0].sequence + 1 : 0;
          
          if (sequence > nextExpected + 10) {
            warnings.push(`Large gap in sequence. Next expected: ${year}${nextExpected.toString().padStart(3, '0')}`);
          }
        }
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings
      };
    } catch (error) {
      console.error('Failed to validate with fresh data, falling back to cached data:', error);
      return validateJobNumber(jobNumber);
    }
  }, [fetchFreshJobNumbers, analyzeJobNumbers, validateJobNumber]);

  // Check if job number exists
  const jobNumberExists = useCallback((jobNumber) => {
    return jobNumberAnalysis.allJobNumbers.has(jobNumber?.toString().trim());
  }, [jobNumberAnalysis.allJobNumbers]);

  // Get job numbers by year
  const getJobNumbersByYear = useCallback((year) => {
    return jobNumberAnalysis.validJobNumbers.filter(job => job.year === year);
  }, [jobNumberAnalysis.validJobNumbers]);

  // Get job numbers by year using fresh data
  const getJobNumbersByYearWithFreshData = useCallback(async (year) => {
    try {
      const freshData = await fetchFreshJobNumbers();
      const freshAnalysis = analyzeJobNumbers(freshData);
      return freshAnalysis.validJobNumbers.filter(job => job.year === year);
    } catch (error) {
      console.error('Failed to get fresh job numbers by year, falling back to cached data:', error);
      return getJobNumbersByYear(year);
    }
  }, [fetchFreshJobNumbers, analyzeJobNumbers, getJobNumbersByYear]);

  return {
    // Standard functions (use cached data)
    getNextJobNumber,
    getRecentJobNumbers,
    validateJobNumber,
    jobNumberExists,
    getJobNumbersByYear,
    allJobNumbers: jobNumberAnalysis.allJobNumbers,
    validJobNumbers: jobNumberAnalysis.validJobNumbers,
    
    // Fresh data functions (fetch latest from API)
    fetchFreshJobNumbers,
    getNextJobNumberWithFreshData,
    getRecentJobNumbersWithFreshData,
    validateJobNumberWithFreshData,
    getJobNumbersByYearWithFreshData,
    analyzeJobNumbers // Exposed for external use with fresh data
  };
};

export default useJobNumberLogic;
